import { AnyObject } from '@triare/auth-redux';
import React, { useEffect } from 'react';
import {
  CheckCircleOutlined, ExclamationCircleFilled, RedoOutlined, SendOutlined, StopOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Tooltip } from 'antd';
import { ButtonProps } from 'antd/es/button/button';
import {
  useChangeUserStatus,
  UserTableRow,
  useUserDelete, useUserForgotPassword,
  useUserResetPassword,
  useUserSendInvitation,
} from '../../../hooks/api/users';
import { useSimpleModal } from '../../Common/Modal/Simple';
import { useAuth, User } from '../../../store/auth';
import { useMessageError, useMessageSuccess } from '../../../hooks/common';
import { listAction } from '../../Common/Table';
import { UserRole, UserStatusEnum } from '../../../enums/user';
import { SendEmailEnum, useInviteUser } from '../../../hooks/api/inviteUsers';
import { capitalize } from '../../../utils';

interface ActionButtonProps<RequestRes = undefined> {
  record: UserTableRow; // Partial<UserTableRow>
  buttonProps?: ButtonProps;
  setTableKey?: (newVal: number) => void;
  afterSuccess?: (res?: RequestRes) => void;
}

export function DeleteButton({
  record, setTableKey, afterSuccess, buttonProps,
}: ActionButtonProps & AnyObject) {
  const userDelete = useUserDelete();
  const { open, contextHolder } = useSimpleModal();
  const { user } = useAuth();

  useMessageError([userDelete]);
  useMessageSuccess([userDelete], 'User deleted successfully');

  useEffect(() => {
    if (!userDelete.error && !userDelete.loading && userDelete.data) {
      setTableKey?.(Date.now());
      afterSuccess?.();
    }
  }, [userDelete.error, userDelete.loading, userDelete.data]);

  const ActionButton = listAction.delete;

  if (user?.id === record.id || record?.role === UserRole.ROOT) {
    return null;
  }

  return (
    <>
      <Tooltip title="Delete user">
        <ActionButton
          loading={userDelete.loading}
          title="Delete user"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            open({
              icon: <ExclamationCircleFilled />,
              title: 'Delete user?',
              content: (
                <span>
                  Are you sure you want to delete user
                  {' '}
                  <b>{record.email}</b>
                  ?
                </span>
              ),
              cancelText: 'Cancel',
              okText: 'Delete',
              okButtonProps: {
                danger: true,
              },
              onOk: () => userDelete.fetch(record.id),
            });
          }}
          {...buttonProps}
        />
      </Tooltip>
      {contextHolder}
    </>
  );
}

export function ReloadButton({ record, afterSuccess, buttonProps }: ActionButtonProps<undefined>) {
  const administratorSendInvitation = useUserSendInvitation();
  const administratorResetPassword = useUserResetPassword();

  useMessageError([administratorSendInvitation, administratorResetPassword]);
  useMessageSuccess([administratorSendInvitation], 'Invitation sent successfully');
  useMessageSuccess([administratorResetPassword], 'Reset-password link sent successfully');

  useEffect(() => {
    if (
      (administratorResetPassword.data && !administratorResetPassword.loading && !administratorResetPassword.error)
      || (
        administratorSendInvitation.data && !administratorSendInvitation.loading && !administratorSendInvitation.error)
    ) {
      afterSuccess?.();
    }
  }, [
    administratorResetPassword.data, administratorResetPassword.loading, administratorResetPassword.error,
    administratorSendInvitation.data, administratorSendInvitation.loading, administratorSendInvitation.error]);

  const ActionButton = listAction.reload;

  return (
    <ActionButton
      loading={administratorSendInvitation.loading}
      title="Reload administrator"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();

        return record.status === 'active' ? (
          administratorResetPassword.fetch(undefined, `${record.id}/reset-password`)
        ) : (
          administratorSendInvitation.fetch(undefined, `${record.id}/invitation`)
        );
      }}
      {...buttonProps}
    />
  );
}

export function DeactivateButton({
  record, setTableKey, afterSuccess, buttonProps,
}: ActionButtonProps<User>) {
  const { user } = useAuth();
  const { open, contextHolder } = useSimpleModal();
  const changeUserStatus = useChangeUserStatus(record.id);

  useMessageError([changeUserStatus]);
  useMessageSuccess([changeUserStatus], 'Action Successful!');
  // useMessageSuccess([administratorResetPassword], 'Reset-password link sent successfully');

  const isDeactivated = record.status === UserStatusEnum.DEACTIVATED;

  const deactivateUser = () => {
    changeUserStatus.fetch({ status: UserStatusEnum.DEACTIVATED });
  };

  const activateUser = () => {
    changeUserStatus.fetch({ status: UserStatusEnum.ACTIVE });
  };

  useEffect(() => {
    if (!changeUserStatus.error && !changeUserStatus.loading && changeUserStatus.data) {
      setTableKey?.(Date.now());
      afterSuccess?.(changeUserStatus.data);
    }
  }, [changeUserStatus.error, changeUserStatus.loading, changeUserStatus.data]);

  if (
    /** Disallow to delete himself and if current record is Root */
    (user?.id === record.id || record?.role === UserRole.ROOT)
    /** Disallow for admin to deactivate other admins */
    || (user?.role === UserRole.ADMIN && record.role === UserRole.ADMIN)
  ) {
    return null;
  }

  return (
    <>
      <Tooltip title={`${isDeactivated ? 'Activate' : 'Deactivate'} user`}>
        <Button
          key="changeStatus"
          type="text"
          icon={isDeactivated ? <CheckCircleOutlined /> : <StopOutlined />}
          loading={changeUserStatus.loading}
          title={`${isDeactivated ? 'Activate' : 'Deactivate'} user`}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();

            open({
              icon: <ExclamationCircleFilled />,
              title: `${isDeactivated ? 'Activate' : 'Deactivate'} user?`,
              content: (
                <span>
                  Are you sure you want to
                  {' '}
                  {isDeactivated ? 'Activate' : 'Deactivate'}
                  {' '}
                  user
                  {' '}
                  <b>{record.email}</b>
                  ?
                </span>
              ),
              cancelText: 'Cancel',
              okText: isDeactivated ? 'Activate' : 'Deactivate',
              okButtonProps: {
                danger: true,
              },
              onOk: () => (isDeactivated ? activateUser() : deactivateUser()),
            });
          }}
          {...buttonProps}
        />
      </Tooltip>
      {contextHolder}
    </>
  );
}

export function ResetPasswordButton({ record, buttonProps }: ActionButtonProps) {
  const userForgotPassword = useUserForgotPassword();
  const { open, contextHolder } = useSimpleModal();

  useMessageError([userForgotPassword]);
  useMessageSuccess([userForgotPassword], 'Password reset performed successfully');

  if (!record.email) {
    return null;
  }

  return (
    <>
      <Tooltip title="Reset password" placement="topRight">
        <Button
          key="changeStatus"
          type="text"
          icon={<RedoOutlined />}
          loading={userForgotPassword.loading}
          title="Reset Password"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();

            open({
              icon: <ExclamationCircleFilled />,
              title: 'Reset password?',
              content: (
                <span>
                  Are you sure you want to reset password for user
                  {' '}
                  <b>{record.email}</b>
                  ?
                </span>
              ),
              cancelText: 'Cancel',
              okText: 'Reset',
              okButtonProps: {
                danger: true,
              },
              onOk: () => userForgotPassword.fetch({ email: record?.email || '' }),
            });
          }}
          {...buttonProps}
        />
      </Tooltip>
      {contextHolder}
    </>
  );
}

// const SelectContracts = () => {};

export function InviteUserButton({ record, buttonProps }: ActionButtonProps) {
  const inviteUser = useInviteUser();
  const { open, contextHolder } = useSimpleModal();

  useMessageError([inviteUser]);
  useMessageSuccess([inviteUser], 'Email sent successfully');

  if (!record.email) {
    return null;
  }

  const confirmAndSend = (type: SendEmailEnum, wantToText: string) => {
    open({
      icon: <SendOutlined />,
      title: `${capitalize(wantToText)}?`,
      content: (
        <>
          Are you sure you want to
          {' '}
          {wantToText}
          {' '}
          <b>{record.email}</b>
          ?
        </>
      ),
      okText: 'Send',
      cancelText: 'Cancel',
      onOk: () => inviteUser.fetch({ type, userId: record.id }),
    });
  };

  // const businessPartnersLoad = useBusinessPartnersGet();
  //
  // useEffect(() => {
  //   businessPartnersLoad.fetch({ userId: record.id, pageSize: 50 });
  // }, [record.id]);
  // const confirmSystemMailSend = () => (type: SendEmailEnum, wantToText: string) => {
  //   open({
  //     icon: <SendOutlined />,
  //     title: `${capitalize(wantToText)}?`,
  //     content: (
  //       <>
  //         Are you sure you want to
  //         {' '}
  //         {wantToText}
  //         {' '}
  //         <b>{record.email}</b>
  //         ?
  //       </>
  //     ),
  //     okText: 'Send',
  //     cancelText: 'Cancel',
  //     onOk: () => inviteUser.fetch({ type, userId: record.id }),
  //   });
  // };

  const menuItems = [
    {
      key: 'invite',
      label: 'Invite User',
      onClick: () => confirmAndSend(SendEmailEnum.CREATE_ACCOUNT, 'invite user'),
    },
    {
      key: 'confirmation',
      label: 'System Mail Kundenportal 2.0',
      onClick: () => confirmAndSend(SendEmailEnum.SYSTEM_MAIL_2_0, 'send system mail 2.0'),
    },
  ];

  return (
    <>
      <Tooltip title="Select sending email" placement="bottomRight">
        <Dropdown
          menu={{ items: menuItems }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<SendOutlined />}
            loading={inviteUser.loading}
            {...buttonProps}
          />
        </Dropdown>
        {/* <Button
          key="changeStatus"
          type="text"
          icon={<SendOutlined />}
          loading={inviteUser.loading}
          title="Invite user"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();

            open({
              icon: <SendOutlined />,
              title: 'Invite user?',
              content: (
                <span>
                  Are you sure you want to invite user
                  {' '}
                  <b>{record.email}</b>
                  ?
                </span>
              ),
              cancelText: 'Cancel',
              okText: 'Invite',
              onOk: () => {
                const payload = { type: SendEmailEnum.CREATE_ACCOUNT, userId: record.id };
                // const searchParams = createSearchParamsString(payload);

                inviteUser.fetch(payload);
              },
            });
          }}
          {...buttonProps}
        /> */}
      </Tooltip>
      {contextHolder}
    </>
  );
}
